import { FormControl, FormHelperText, InputLabel, ListItemText, MenuItem, Select, Typography } from '@mui/material';
import { useId } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { z } from 'zod';

export interface ISelectInputProps {
	name: string;
	label: string;
	required?: boolean;
	formHelperText?: string;
	options: {
		label: string;
		value: string | number;
	}[];
	disabled?: boolean;
	sx?: Record<string, any>;
}

type ValueOf<T> = T[keyof T];

export const SelectInputSchema = ((options) => {
	return {
		options,
		create: (fieldName: string, validation: ValueOf<typeof options>) => {
			return z.object({ [fieldName]: validation });
		},
	};
})({
	default: z.string().min(1, 'Required'),
	notRequired: z.string(),
});

const getColor = (error: any, disabled: boolean) => {
	if (disabled) {
		return 'text.disabled';
	}
	if (error) {
		return 'error.dark';
	}
	return 'text.primary';
};

export default function SelectInput({
	label,
	name,
	required = true,
	formHelperText,
	options,
	disabled = false,
}: ISelectInputProps) {
	const { control } = useFormContext();
	const labelId = useId();

	const {
		field,
		formState: { errors },
	} = useController({ name, control });

	const error = errors[name];

	return (
		<FormControl
			fullWidth
			error={!!error}
			required={required}
			sx={{
				'& .MuiInputLabel-root': {
					color: getColor(error, disabled),
					fontWeight: 400,
				},
			}}
		>
			<InputLabel id={labelId} disabled={disabled}>
				{label}
			</InputLabel>
			<Select label={label} labelId={labelId} {...field} disabled={disabled}>
				{Object.keys(options).length > 0 &&
					options?.map((option) => (
						<MenuItem value={option.value} key={option.value}>
							<ListItemText>
								<Typography variant="bodyMediumBook">{option.label}</Typography>
							</ListItemText>
						</MenuItem>
					))}
			</Select>
			<FormHelperText>
				<>{error?.message || formHelperText}</>
			</FormHelperText>
		</FormControl>
	);
}
